import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import { SparkleIcon } from "./BrandIcons";

/**
 * Editorial, on-brand service-detail pop-out.
 * `detail` shape (admin-managed):
 *   { group_title, eyebrow, hero_image, footer_note, cta_text, cta_link, items: [{ name, price, duration, description }] }
 */
export default function ServiceDetailModal({ open, onClose, detail }) {
  const closeBtnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    setTimeout(() => closeBtnRef.current?.focus(), 0);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open || !detail) return null;

  const title = detail.group_title || detail.title || "Service details";
  const eyebrow = detail.eyebrow || "Service Details";
  const ctaText = detail.cta_text || "Enquire now";
  const ctaLink = detail.cta_link || "/contact#enquiry-form";
  const items = detail.items || [];

  return (
    <div
      className="fixed inset-0 z-[90] bg-ink/70 backdrop-blur-sm flex items-stretch md:items-center justify-center px-0 md:px-6 py-0 md:py-10"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="service-detail-modal"
    >
      <div
        className="relative w-full md:max-w-3xl bg-cream border-0 md:border-2 md:border-ink md:shadow-brutLg flex flex-col max-h-screen md:max-h-[90vh] overflow-hidden animate-fadeUp"
        onClick={(e) => e.stopPropagation()}
        data-testid="service-detail-modal-card"
      >
        {/* Header (sticky) — extra vertical breathing room above + below the title */}
        <div className="flex items-start justify-between gap-4 px-5 py-6 md:px-8 md:py-8 border-b-2 border-ink bg-pinkpanel relative overflow-hidden">
          <div className="speckle absolute inset-0 pointer-events-none opacity-60" />
          <div className="relative z-10 min-w-0 pr-2">
            <p className="eyebrow text-ink/80 text-[0.65rem]">{eyebrow}</p>
            <h2 className="heading mt-2 text-xl md:text-2xl text-ink truncate" data-testid="service-detail-title">{title}</h2>
          </div>
          <button
            ref={closeBtnRef}
            onClick={onClose}
            className="relative z-10 shrink-0 h-10 w-10 grid place-items-center border-2 border-ink bg-cream hover:bg-coral transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ink"
            aria-label="Close service details"
            data-testid="service-detail-close"
          >
            <X className="h-5 w-5 text-ink" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto" data-testid="service-detail-body">
          {detail.hero_image && (
            // Subtle gap between the pink header and the hero image (client request)
            <div className="pt-4 md:pt-6 px-0 md:px-0 bg-cream">
              <div className="aspect-[16/8] border-t-2 border-b-2 border-ink overflow-hidden bg-pinkpanel">
                <img
                  src={detail.hero_image}
                  alt={title}
                  className="w-full h-full object-cover mix-blend-multiply"
                  loading="lazy"
                />
              </div>
            </div>
          )}

          <ul className="divide-y-2 divide-ink/10">
            {items.map((it, i) => (
              <li key={it.id || i} className="px-5 md:px-8 py-5 md:py-6" data-testid={`service-detail-item-${i}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                  <h3 className="heading text-base md:text-lg text-ink">{it.name}</h3>
                  {it.price && <span className="font-heading text-lg md:text-xl text-ink tracking-wide whitespace-nowrap">{it.price}</span>}
                </div>
                {it.duration && (
                  <p className="mt-1 eyebrow text-[0.65rem] text-ink/60">{it.duration}</p>
                )}
                {(it.description || it.notes) && (
                  <p className="mt-2 text-sm md:text-[0.95rem] text-ink/85 leading-relaxed">{it.description || it.notes}</p>
                )}
              </li>
            ))}
          </ul>

          {detail.footer_note && (
            <div className="px-5 md:px-8 py-5 md:py-6 bg-cream border-t-2 border-ink/10">
              <p className="text-xs md:text-sm text-ink/65 italic leading-relaxed">* {detail.footer_note}</p>
            </div>
          )}
        </div>

        {/* Sticky CTA footer */}
        <div className="border-t-2 border-ink bg-cream px-5 md:px-8 py-4 md:py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-ink/80">Don’t see what you need? <span className="hidden sm:inline">Tell me about the vibe.</span></p>
          <Link
            to={ctaLink}
            onClick={onClose}
            className="btn-brut"
            data-testid="service-detail-enquire-btn"
          >
            {ctaText} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Decorative sparkle */}
        <div className="absolute right-4 bottom-20 text-ink/30 hidden md:block">
          <SparkleIcon className="h-6 w-6" />
        </div>
      </div>
    </div>
  );
}
